import { CheckCircleIcon, InfoCircleIcon } from '@lastbrand/web-ui/icons'
import { Tooltip, TooltipProvider } from '@lastbrand/web-ui/tooltip'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'

import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { isProductAlcohol } from '@src/quince-site-common/src/helpers/productHelper'
import * as svdCustomFields from '@src/quince-site-common/src/helpers/svd-custom-fields'
import useEddExp from '@src/quince-site-common/src/hooks/edd/hooks/useEddExp'
import {
  generateEDDLabel,
  getCutoffTime,
  parseDateFromEddLabel,
} from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import {
  boldClass,
  calloutClass,
  cutoffStackedGapClass,
  desktopOnlyMsgClass,
  eddDefaultContainerClass,
  eddMessageWithCutoffClass,
  eddMessageWrapperClass,
  eddMessageWrapperNewDesignV2Class,
  eddMessageWrapperQuickATCClass,
  errorMessageWrapperClass,
  errorMessageWrapperWrapStylingClass,
  hideDesktopMsgClass,
  infoIconClass,
  messageIconClass,
  unableToShipActionsWrapStylingClass,
  zipLinkCutoffCss,
  zipLinkUnableToShipCss,
} from './edd-message-container-ds.styles'
import { OrderCutoffTimeDS } from './order-cutoff-time-ds'
import { ZipCodeButtonDS } from './zip-code-button-ds'

export const DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS = 'disableForceEDDForAgeRestrictedProducts'

const INFO_ICON_SIZE = 17

interface EDDMessageContainerDSProps {
  eddDetail: EDDDetails | undefined
  product: Product
  selectedInternalVariantId: number[] | undefined
  isQuickATCForMobile?: boolean
  containerClassName?: string
  showIcon?: boolean
  holidayMessageShown?: boolean
  // SVD flow: read the age-restriction custom field SVD-native. Threaded from `ProductCTADS`; defaults
  // false so miniPDP / legacy renders stay on the legacy read.
  isSvd?: boolean
}

/**
 * Design-System twin of EDDMessageContainer. Prop-compatible with the legacy
 * component, rendered only on the DS path (no internal gate). All EDD helpers
 * and hooks are reused verbatim; only the markup/styling moves to Panda + web-ui.
 */
export function EDDMessageContainerDS({
  eddDetail,
  product,
  selectedInternalVariantId,
  isQuickATCForMobile,
  containerClassName,
  showIcon,
  holidayMessageShown = false,
  isSvd = false,
}: EDDMessageContainerDSProps) {
  const { zipCode } = useZipCode()
  const { isEddExpEnabled: EDDExpEnabled } = useEddExp()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const localeConfig = useStatsigDynamicLocaleConfig()

  // Check if EDD wrapping should be applied based on locale config
  const shouldApplyWrapping =
    localeConfig.edd?.wrapping?.enabled && (!isDesktop || !localeConfig.edd.wrapping.excludeDesktop)

  const selectedVariant = selectedInternalVariantId?.[0]?.toString()
  const selectedVariantObj = selectedInternalVariantId?.[0]
    ? product.variants?.find(variant => variant.id === selectedInternalVariantId[0])
    : undefined

  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled =
    EDDExpEnabled ||
    ((isSvd
      ? svdCustomFields.getSvdIsAgeRestrictedProduct(product)
      : productHelper.getIsAgeRestrictedProduct(product)) &&
      !disableForceEDD) // force edd for age restricted products

  const { t } = useTranslation('common', { keyPrefix: 'edd' })
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    if (eddDetail?.showEDD) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        // @i18n-ignore
        action: 'product_page_edd',
        // @i18n-ignore
        category: 'edd',
        context: {
          product_id: product.spid,
          variant_id: selectedVariantObj?.svid,
          internal_variant_id: selectedInternalVariantId?.[0],
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          edd_standard_eligible: eddDetail.type === 'LB_STANDARD',
          edd_express_eligible: eddDetail.type === 'LB_SUPER',
          edd_max_days: eddDetail.max_days,
          edd_min_days: eddDetail.min_days,
          edd_max_date: eddDetail.max_date,
          edd_min_date: eddDetail.min_date,
          location_ids: eddDetail.inventoryLocations,
          productProperties: getProductPropertiesForTracking(product, selectedVariant),
          edd_label: generateEDDLabel(eddDetail, tCommon),
          is_serviceable: eddDetail.isServiceable,
          zip_code: zipCode,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eddDetail?.showEDD,
    eddDetail?.type,
    eddDetail?.max_days,
    eddDetail?.min_days,
    product.id,
    selectedVariant,
    selectedInternalVariantId,
    eddDetail?.max_date,
    eddDetail?.min_date,
    eddDetail?.inventoryLocations,
  ])

  if (!isEDDExpEnabled || eddDetail?.showEDD === undefined) return null

  const wrapperClass = (...extra: (string | false | undefined)[]) =>
    cx(
      eddMessageWrapperClass,
      containerClassName,
      isQuickATCForMobile && eddMessageWrapperQuickATCClass,
      eddMessageWrapperNewDesignV2Class,
      ...extra,
    )

  // (a) no-zip
  if (!zipCode) {
    return (
      <div className={wrapperClass()}>
        {/* STRFRNT-16892: same flex icon+text shape as the default (b) variant — the icon was
            inline in the text flow here (vertical-align baseline math), misaligning it vs Figma
            8719:17323. One structural pattern across all five variants, no pixel nudges. */}
        <div className={showIcon ? eddDefaultContainerClass : undefined}>
          {showIcon && <CheckCircleIcon width={20} height={20} className={messageIconClass} data-testid="icon" />}
          <div>
            {t('in_stock_enter_zip_code')}
            <ZipCodeButtonDS>{t('zip_code')}</ZipCodeButtonDS> {t('for_delivery_info')}
          </div>
        </div>
      </div>
    )
  }

  // (f) unable-to-ship
  if (!eddDetail.isServiceable) {
    const zipCodeButton = <ZipCodeButtonDS cssOverride={zipLinkUnableToShipCss}>{t('update_zip_code')}</ZipCodeButtonDS>
    const unableToShipMsg = isProductAlcohol(product) ? t('unable_to_ship_wine_msg') : t('unable_to_ship_msg')
    const tooltip = (
      <TooltipProvider>
        <Tooltip textLabel={unableToShipMsg} aria-label={unableToShipMsg} position="top">
          <InfoCircleIcon
            width={INFO_ICON_SIZE}
            height={INFO_ICON_SIZE}
            className={infoIconClass}
            data-testid="info-icon"
            aria-label={unableToShipMsg}
          />
        </Tooltip>
      </TooltipProvider>
    )

    return (
      <div
        data-testid="unable-to-ship"
        className={wrapperClass(errorMessageWrapperClass, shouldApplyWrapping && errorMessageWrapperWrapStylingClass)}
      >
        <div data-testid="unable-to-ship-message">{t('unable_to_ship', { zipCode })}</div>
        {shouldApplyWrapping ? (
          <div className={unableToShipActionsWrapStylingClass}>
            {zipCodeButton}
            {tooltip}
          </div>
        ) : (
          <>
            {' '}
            {zipCodeButton}
            {tooltip}
          </>
        )}
      </div>
    )
  }

  // Check if cutoff time has expired (cutoffDate exists but getCutoffTime returns null)
  const isCutoffExpired = eddDetail.cutoffDate && !getCutoffTime(eddDetail.cutoffDate, t)
  // (e)/(g) no-EDD-but-zip OR cutoff expired -> "in stock ships free to"
  if ((!eddDetail.showEDD && zipCode) || isCutoffExpired) {
    return (
      <div className={wrapperClass()}>
        {/* STRFRNT-16892: flex icon+text shape — see the no-zip variant's comment. */}
        <div className={showIcon ? eddDefaultContainerClass : undefined}>
          {showIcon && <CheckCircleIcon width={20} height={20} className={messageIconClass} data-testid="icon" />}
          <div>
            {t('in_stock_and_ships_free_to')} <ZipCodeButtonDS>{zipCode}</ZipCodeButtonDS>
          </div>
        </div>
      </div>
    )
  }

  // (c) express shipping
  if (eddDetail.isExpressShipping) {
    return (
      <div className={wrapperClass()}>
        {/* STRFRNT-16892: flex icon+text shape — see the no-zip variant's comment. */}
        <div className={showIcon ? eddDefaultContainerClass : undefined}>
          {showIcon && <CheckCircleIcon width={20} height={20} className={messageIconClass} data-testid="icon" />}
          <div>
            {generateEDDLabel(eddDetail, tCommon)} {tCommon('with_priority_shipping_to')}{' '}
            <ZipCodeButtonDS>{zipCode}</ZipCodeButtonDS>
          </div>
        </div>
      </div>
    )
  }

  // (d) premium 2-day callout
  if (eddDetail.type === 'LB_STANDARD_PREMIUM' && eddDetail.max_cal_days === 2) {
    const label = generateEDDLabel(eddDetail, tCommon) ?? ''
    const { before, date, after } = parseDateFromEddLabel(label)
    return (
      <div className={wrapperClass()}>
        <div className={calloutClass}>
          <div>
            {before}
            <strong className={boldClass}>{date}</strong>
            {after}
          </div>
          {eddDetail.cutoffDate && <OrderCutoffTimeDS cutoffDate={eddDetail.cutoffDate} />}
        </div>
        {/* STRFRNT-16892: flex icon+text shape — see the no-zip variant's comment. */}
        <div className={showIcon ? eddDefaultContainerClass : undefined}>
          {showIcon && <CheckCircleIcon width={20} height={20} className={messageIconClass} data-testid="icon" />}
          <div>
            {t('in_stock_and_ships_free_to')} <ZipCodeButtonDS>{zipCode}</ZipCodeButtonDS>
          </div>
        </div>
      </div>
    )
  }

  // (b) serviceable + EDD (default): generateEDDLabel + 'to' + zip + OrderCutoffTime
  const eddLabel = generateEDDLabel(eddDetail, tCommon) ?? ''
  const toText = tCommon('to')
  const cutoffTimeText = eddDetail.cutoffDate ? (getCutoffTime(eddDetail.cutoffDate, t) ?? '') : ''
  const messageText = `${eddLabel} ${toText} ${zipCode}${cutoffTimeText ? ` . ${cutoffTimeText}` : ''}`
  // Only apply cutoff style on desktop when message length <= 69 and not in cutoff wrap locales
  const shouldApplyCutoffStyle =
    holidayMessageShown && isDesktop && !localeConfig.edd?.cutoffWrap && messageText.length <= 69

  return (
    <div className={showIcon ? eddDefaultContainerClass : ''}>
      {showIcon && <CheckCircleIcon width={20} height={20} className={messageIconClass} data-testid="icon" />}
      <div className={wrapperClass()}>
        <div className={shouldApplyCutoffStyle ? eddMessageWithCutoffClass : ''}>
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('to')}{' '}
          <ZipCodeButtonDS cssOverride={shouldApplyCutoffStyle ? zipLinkCutoffCss : undefined}>
            {zipCode}
          </ZipCodeButtonDS>
          {eddDetail.cutoffDate && (
            <>
              <span className={shouldApplyCutoffStyle ? desktopOnlyMsgClass : hideDesktopMsgClass}>{'.'}</span>
              <OrderCutoffTimeDS
                cutoffDate={eddDetail.cutoffDate}
                className={shouldApplyCutoffStyle ? undefined : cutoffStackedGapClass}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EDDMessageContainerDS
